import {Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef} from "@angular/core";
import {LoginServerService} from "src/app/layouts/public/login/services/login-server.service";
import {AccountPermissionAccess, PermissionAccess} from "src/app/shared/models/PermissionAccess";
import {Subscription} from 'rxjs/internal/Subscription';
import {PermissionHelper} from "src/app/helpers/permission.helper";

@Directive({
    selector: '[permission]',
    standalone: true
})
export class PermissionDirective implements OnInit, OnDestroy {

    constructor(
        private templateRef: TemplateRef<any>,
        private viewContainerRef: ViewContainerRef, 
        public loginService: LoginServerService
    ) {}

    @Input() permission: PermissionAccess;
    @Input() accountPermission: AccountPermissionAccess;
    
    public subscription: Subscription;
    
    ngOnInit() {
        this.onChange();

        this.subscription = this.loginService.currentAccountChangedNotify.subscribe(() => {
            this.onChange();
        });
    }
    
    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    private onChange() {
        var isPermitted = this.permission ? PermissionDirective.checkPermission(this.permission) : PermissionDirective.checkAccountPermission(this.accountPermission);
        this.viewContainerRef.clear();
        if (isPermitted) {
            this.viewContainerRef.createEmbeddedView(this.templateRef);
        }
    }
    
    public static checkPermission(permission: PermissionAccess) {
        if (!permission) {
            return false;            
        }
        // Break out if user has all permissions
        if (!permission.ignoreSuper && PermissionHelper.isSuperUser()) {
            return true;
        }

        let hasOneOfThesePermissions = permission.permission ? this.hasOneOfThesePermissions(permission.permission) : true;
        let isOneOfTheseAccounts = permission.accountType ? this.isOneOfTheseAccounts(permission.accountType) : true;
        let checkSubscriptions = permission.requireSub ? PermissionHelper.hasSubs() : true;

        return hasOneOfThesePermissions && isOneOfTheseAccounts && checkSubscriptions;
    }

    public static checkAccountPermission(accountPermission: AccountPermissionAccess) {
        // Break out if user has all permissions
        if (PermissionHelper.isSuperUser()) {
            return true;
        }
        
        let hasPermission: boolean[] = [];
        if (accountPermission && accountPermission.accountPermission) {
            accountPermission.accountPermission.forEach((accPerm) => {
                hasPermission.push(this.checkPermission(accPerm));
            });
        }

        if (hasPermission) {
            return hasPermission.some((value) => value === true);
        }
        
        return false;
    }

    private static isOneOfTheseAccounts(accounts: string[]) {
        if (PermissionHelper.isGlobalSupport())
            return true;
        
        let isAccount: boolean[] = [];

        accounts.forEach((acc) => {
            switch (acc) {
                case 'new':
                    isAccount.push(PermissionHelper.isNewUser());
                    break;
                case 'account':
                    isAccount.push(PermissionHelper.isAccountUser());
                    break;
                case 'site':
                    isAccount.push(PermissionHelper.isSiteUser());
                    break;
                case 'processingsite':
                    isAccount.push(PermissionHelper.isSiteUser() && PermissionHelper.isProcessingSite());
                    break;
                case 'group':
                    isAccount.push(PermissionHelper.isGroupUser());
                    break;
                case 'distributor':
                    isAccount.push(PermissionHelper.isDistributorUser());
                    break;
                case 'globalsupport':
                    isAccount.push(PermissionHelper.isGlobalSupport());
                    break;
                case 'staff':
                    isAccount.push(PermissionHelper.isStaffUser());
                    break;
                case 'legacystaff':
                    isAccount.push(PermissionHelper.isOldStaffUser());
                    break;
                case 'markupstaff':
                    isAccount.push(PermissionHelper.isOldMarkupStaff());
                    break;
                default:
                    isAccount.push(false);
                    break;
            }
        });

        if (isAccount) {
            return isAccount.some((value) => value === true);
        }

        return false;
    }

    private static hasOneOfThesePermissions(permissions: string[]) {
        let hasPermission: boolean[] = [];

        permissions.forEach((perm) => {
            hasPermission.push(PermissionHelper.hasPermission(perm));
        });

        if (hasPermission) {
            return hasPermission.some((value) => value === true);
        }

        return false;
    }
}
